import React, { useEffect, useRef, useState } from "react";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";

const GOOGLE_LIBRARIES: ("places")[] = ["places"];

export type LocationPoint = { city: string; count: number; lat: number; lng: number };
type PixelPos = { x: number; y: number };

// Marker diameter in px, scaled by count (clamped to a sane range).
const markerSize = (count: number) => Math.min(56, Math.max(28, 20 + Math.sqrt(count) * 5));

// Floor applied to fitBounds()'s result (see fitMapToLocations) so narrow
// mobile-width map cards don't zoom out further than this.
const MIN_FIT_ZOOM = 6;

// Must be a stable reference, not an inline object literal in JSX — GoogleMap
// treats `center` as a controlled prop and re-applies it via map.setCenter()
// whenever the prop reference changes. A literal recreated on every render
// looks like a "changed" center on every single re-render of this page, so
// the map kept getting silently recentered back to Gujarat almost
// immediately after any manual drag/zoom, making panning look broken.
const MAP_CENTER = { lat: 22.5, lng: 71.5 };
const MAP_OPTIONS: google.maps.MapOptions = {
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
    // draggable stays true at rest — leaving it permanently false also
    // silently disables scroll-wheel zooming on this map (confirmed via an
    // isolated test), which isn't obviously related but is real. The drag
    // effect below instead flips this to false for the duration of an
    // actual drag gesture only (via a capture-phase mousedown, so it runs
    // before Google's own drag-start handling), so native dragging never
    // engages but wheel-zoom keeps working whenever nothing is being
    // dragged.
    draggable: true,
    draggableCursor: "grab",
    draggingCursor: "grabbing",
    // Lets the scroll wheel zoom directly (default "cooperative" handling
    // requires holding Ctrl, showing a "use ctrl+scroll" hint instead).
    gestureHandling: "greedy",
};

type LocationMapProps = {
    title: string;
    loading: boolean;
    locations: LocationPoint[];
    subtitle: string;
    unitLabel: string; // e.g. "user" or "active user" — pluralized as needed
    // When false, skips fitBounds entirely and keeps the map at its initial
    // MAP_CENTER/zoom=6 view. Useful for sparse/realtime data (e.g. only 1-2
    // active users right now) where fitting bounds to just those points
    // would zoom in far tighter than makes sense, jumping around
    // unpredictably as the data changes. Defaults to true (fit to data),
    // which is the right behavior for larger, denser datasets.
    autoFit?: boolean;
    // Caps the zoom fitBounds can reach — mainly for single-point use (e.g.
    // one profile's current location), where fitting to a single coordinate
    // otherwise zooms in to Google's max (building-level), tighter than
    // useful for "where roughly is this person".
    maxFitZoom?: number;
    // Hides the side "Top Cities" list and lets the map take the full
    // width — appropriate when there's only ever one point to show (a
    // single profile) rather than a ranked multi-city breakdown.
    showCityList?: boolean;
    // Optional — fires when a "Top Cities" row is clicked (in addition to
    // panning the map there, which always happens). Omitted entirely on
    // maps where a per-city drill-down doesn't make sense (e.g. a single
    // profile's own location).
    onCityClick?: (city: string) => void;
    // Optional — when set, replaces the "Top Cities" list in the side panel
    // with this content (same slot, same size/scroll container). The caller
    // owns the content entirely (e.g. a user list + "back to cities"
    // button) — LocationMap itself has no opinion on what a drill-down
    // looks like.
    citySidebarContent?: React.ReactNode;
};

const LocationMap: React.FC<LocationMapProps> = ({ title, loading, locations, subtitle, unitLabel, autoFit = true, maxFitZoom, showCityList = true, onCityClick, citySidebarContent }) => {
    const [activeCity, setActiveCity] = useState<string | null>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const mapWrapperRef = useRef<HTMLDivElement | null>(null);
    const locationsRef = useRef<LocationPoint[]>([]);
    const projectionOverlayRef = useRef<google.maps.OverlayView | null>(null);
    // Pixel positions are computed manually via a projection-only overlay
    // (see recomputePixelPositions) instead of rendering google.maps.Marker /
    // OverlayView directly — this project's Maps API key renders in a vector
    // mode under which those classes attach successfully (their onAdd/onLoad
    // fires) but never paint anything visible, even Google's own default
    // marker icon (confirmed both in automated testing and directly
    // in-browser). Manually projecting lat/lng to container pixels and
    // drawing plain positioned divs sidesteps that entirely since it doesn't
    // depend on any marker/overlay pane.
    const [pixelPositions, setPixelPositions] = useState<Record<string, PixelPos>>({});

    const { isLoaded: mapsLoaded, loadError: mapsLoadError } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY || "",
        libraries: GOOGLE_LIBRARIES,
    });

    useEffect(() => {
        locationsRef.current = locations;
    }, [locations]);

    const recomputePixelPositions = () => {
        const overlay = projectionOverlayRef.current;
        const projection = overlay?.getProjection();
        if (!projection) return;
        const next: Record<string, PixelPos> = {};
        locationsRef.current.forEach((loc) => {
            const point = projection.fromLatLngToContainerPixel(new google.maps.LatLng(loc.lat, loc.lng));
            if (point) next[loc.city] = { x: point.x, y: point.y };
        });
        setPixelPositions(next);
    };

    // Polling instead of relying solely on map events — this project's Maps
    // API key renders in a vector mode where event wiring (draw/idle) proved
    // unreliable for keeping custom overlays in sync during interaction. A
    // cheap fixed-interval recompute guarantees markers track the viewport
    // regardless of what triggered the change (drag, wheel zoom, buttons,
    // keyboard), independent of which events actually fire here.
    useEffect(() => {
        if (!mapsLoaded) return undefined;
        const interval = setInterval(() => {
            if (projectionOverlayRef.current) recomputePixelPositions();
        }, 150);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapsLoaded]);

    const fitMapToLocations = () => {
        if (!autoFit || !mapRef.current || locations.length === 0 || !(window as any).google) return;
        // Fit around points with more than one user — lone single-user
        // outliers (e.g. someone registered from another state) shouldn't
        // drag the initial zoom out to cover half the country. They still
        // render as markers, just don't dictate the default view.
        const significant = locations.filter((loc) => loc.count > 1);
        const boundsSource = significant.length > 0 ? significant : locations;
        const bounds = new google.maps.LatLngBounds();
        boundsSource.forEach((loc) => bounds.extend({ lat: loc.lat, lng: loc.lng }));
        const map = mapRef.current;
        // fitBounds() computes zoom from the bounds' geographic span AND the
        // map's current pixel width — the same bounds need a lower (more
        // zoomed-out) zoom to fit a narrow mobile-width card than a wide
        // desktop one. That's technically correct but looks too zoomed out
        // on phones, so clamp the result back up to a sane floor.
        google.maps.event.addListenerOnce(map, "bounds_changed", () => {
            const zoom = map.getZoom() ?? 0;
            if (zoom < MIN_FIT_ZOOM) map.setZoom(MIN_FIT_ZOOM);
            else if (maxFitZoom != null && zoom > maxFitZoom) map.setZoom(maxFitZoom);
        });
        map.fitBounds(bounds);
    };

    const flyToCity = (loc: LocationPoint) => {
        if (!mapRef.current) return;
        mapRef.current.panTo({ lat: loc.lat, lng: loc.lng });
        mapRef.current.setZoom(11);
        setActiveCity(loc.city);
    };

    useEffect(() => {
        fitMapToLocations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locations, mapsLoaded]);

    const handleMapLoad = (map: google.maps.Map) => {
        mapRef.current = map;
        fitMapToLocations();

        // draw() is intentionally a no-op — OverlayView calls it continuously
        // (every animation frame) while the map is being dragged, and wiring
        // that straight to setState() fought with the browser's native drag
        // handling badly enough that the map stopped panning by hand. We
        // only need the projection this overlay exposes, not live per-frame
        // redraws, so positions are recomputed once via "idle" below instead
        // — it fires only after a pan/zoom/fit actually settles.
        const overlay = new google.maps.OverlayView();
        overlay.onAdd = () => undefined;
        overlay.onRemove = () => undefined;
        overlay.draw = () => undefined;
        overlay.setMap(map);
        projectionOverlayRef.current = overlay;

        // fitBounds() resolves asynchronously (it pans/zooms after this
        // function returns). "idle" fires once the map has settled after
        // every viewport change — including that one — so re-anchoring
        // positions there keeps them in sync no matter what triggered the
        // change (initial fit, a later locations update, manual pan/zoom).
        map.addListener("idle", recomputePixelPositions);
    };

    // Confines dragging to the map's own boundary. Google's native drag
    // tracking can't be interrupted once it starts (it isn't re-checked
    // continuously, so toggling `draggable` or releasing pointer capture
    // mid-gesture has no effect), so panning is implemented here instead via
    // map.panBy(), which we call ourselves — movement only happens for
    // mouse deltas that occur while the cursor is actually over the map,
    // and simply stops (no more panBy calls) the instant it isn't.
    //
    // Native dragging is switched off only for the duration of the drag
    // itself (leaving it off permanently also silently disables wheel-zoom
    // on this map — see MAP_OPTIONS). onDown runs in the CAPTURE phase so it
    // fires before Google's own mousedown handling, preempting native drag
    // from ever starting for this gesture.
    useEffect(() => {
        const wrapper = mapWrapperRef.current;
        if (!wrapper) return undefined;

        let dragging = false;
        let lastX = 0;
        let lastY = 0;

        const isInside = (x: number, y: number) => {
            const rect = wrapper.getBoundingClientRect();
            return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
        };

        const onDown = (e: MouseEvent) => {
            if (!isInside(e.clientX, e.clientY)) return;
            // Skip Google's own UI controls (zoom +/-, pan arrows, etc.) —
            // they all render inside the map area, and even a 1px accidental
            // panBy() while clicking one makes Google treat the click as a
            // drag and suppress the button's action (e.g. zoom stops
            // responding). They're all real <button> elements with a title
            // attribute, unlike the map's own tile/drag surface.
            const target = e.target as HTMLElement;
            if (target.closest('button, [role="button"], [title]')) return;
            dragging = true;
            lastX = e.clientX;
            lastY = e.clientY;
            mapRef.current?.setOptions({ draggable: false });
        };
        const onMove = (e: MouseEvent) => {
            if (!dragging || !mapRef.current) return;
            if (isInside(e.clientX, e.clientY)) {
                mapRef.current.panBy(lastX - e.clientX, lastY - e.clientY);
            }
            // Update the reference point even while outside the map, so
            // panning resumes smoothly (no jump) if the cursor re-enters
            // while the button is still held, instead of a big jolt.
            lastX = e.clientX;
            lastY = e.clientY;
        };
        const onUp = () => {
            dragging = false;
            mapRef.current?.setOptions({ draggable: true });
        };

        wrapper.addEventListener("mousedown", onDown, true);
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => {
            wrapper.removeEventListener("mousedown", onDown, true);
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
    }, []);

    const unitFor = (count: number) => `${unitLabel}${count === 1 ? "" : "s"}`;

    return (
        <div className="card h-full">
            <Card title={title}>
                <p className="text-xs m-0 mb-3" style={{ color: "#9ca3af" }}>
                    {loading ? "Loading…" : subtitle}
                </p>
                <div className="flex flex-col lg:flex-row gap-4">
                    <div style={{ flex: showCityList ? "2 1 auto" : "1 1 auto", minWidth: 0 }}>
                        {mapsLoadError ? (
                            <div style={{ height: "420px", display: "flex", alignItems: "center", justifyContent: "center", background: "#f9fafb", borderRadius: "8px", color: "#9ca3af", fontSize: "13px" }}>
                                Map failed to load
                            </div>
                        ) : !mapsLoaded || loading ? (
                            <Skeleton height="420px" className="rounded" />
                        ) : (
                            <div
                                ref={mapWrapperRef}
                                style={{ position: "relative", width: "100%", height: "420px" }}
                            >
                                <GoogleMap
                                    mapContainerStyle={{ width: "100%", height: "420px", borderRadius: "8px" }}
                                    center={MAP_CENTER}
                                    zoom={6}
                                    onLoad={handleMapLoad}
                                    options={MAP_OPTIONS}
                                />
                                <div style={{ position: "absolute", inset: 0, overflow: "hidden", borderRadius: "8px", pointerEvents: "none" }}>
                                    {/* Rendered smallest-first so bigger, more significant circles paint
                                        on top instead of being hidden under overlapping small ones. */}
                                    {[...locations].sort((a, b) => a.count - b.count).map((loc) => {
                                        const pos = pixelPositions[loc.city];
                                        if (!pos) return null;
                                        const size = markerSize(loc.count);
                                        return (
                                            <div
                                                key={loc.city}
                                                onClick={() => setActiveCity(activeCity === loc.city ? null : loc.city)}
                                                title={`${loc.city}: ${loc.count.toLocaleString()} ${unitFor(loc.count)}`}
                                                style={{
                                                    position: "absolute",
                                                    left: pos.x - size / 2,
                                                    top: pos.y - size / 2,
                                                    width: size,
                                                    height: size,
                                                    borderRadius: "50%",
                                                    background: "#264164",
                                                    border: "2px solid #ffffff",
                                                    boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "#ffffff",
                                                    fontWeight: 700,
                                                    fontSize: Math.max(10, Math.min(14, size * 0.3)),
                                                    cursor: "pointer",
                                                    userSelect: "none",
                                                    pointerEvents: "auto",
                                                }}
                                            >
                                                {loc.count}
                                            </div>
                                        );
                                    })}
                                    {activeCity && pixelPositions[activeCity] && (() => {
                                        const loc = locations.find((l) => l.city === activeCity);
                                        if (!loc) return null;
                                        const pos = pixelPositions[activeCity];
                                        const size = markerSize(loc.count);
                                        return (
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    left: pos.x,
                                                    top: pos.y - size / 2 - 10,
                                                    transform: "translate(-50%, -100%)",
                                                    background: "#1f2937",
                                                    color: "#ffffff",
                                                    padding: "6px 10px",
                                                    borderRadius: "6px",
                                                    fontSize: "12px",
                                                    whiteSpace: "nowrap",
                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                                                }}
                                            >
                                                <strong>{loc.city}</strong>: {loc.count.toLocaleString()} {unitFor(loc.count)}
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        )}
                    </div>
                    {showCityList && (
                        <div className="w-full lg:w-auto lg:min-w-[220px]" style={{ flex: "1 1 auto", maxHeight: "420px", overflowY: citySidebarContent ? "hidden" : "auto" }}>
                            {citySidebarContent ? (
                                citySidebarContent
                            ) : (
                                <>
                                    <p className="text-xs m-0 mb-2" style={{ color: "#6b7280", fontWeight: 700 }}>Top Cities</p>
                                    {loading ? (
                                        Array.from({ length: 6 }).map((_, i) => (
                                            <Skeleton key={i} height="1.5rem" className="rounded mb-2" />
                                        ))
                                    ) : locations.length === 0 ? (
                                        <p className="text-sm m-0" style={{ color: "#9ca3af" }}>No location data yet</p>
                                    ) : (
                                        <div className="flex flex-col">
                                            {locations.map((loc) => (
                                                <div
                                                    key={loc.city}
                                                    className="flex justify-between items-center text-xs cursor-pointer gap-2"
                                                    style={{ color: "#374151", padding: "6px 0", borderBottom: "1px solid #e5e7eb" }}
                                                    onClick={() => { flyToCity(loc); onCityClick?.(loc.city); }}
                                                >
                                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                                                        {loc.city}
                                                    </span>
                                                    <span style={{ fontWeight: 600, flexShrink: 0 }}>{loc.count.toLocaleString()}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default LocationMap;
